import type {
  ColumnGroupPreferenceState,
  ColumnPreferenceState,
  IBlotterPreferenceState,
} from 'components/shared/Blotter/Grid/blotter.model';
import type { IBlotterPriceImprovementItem } from 'models/IBlotterItem';
import type { ApiError } from 'utils/http/apiErrorHelper';

import type { IBlotterColumnDefinition } from '../common/blotter.model';
import type {
  IPriceImprovementBlotterFilters,
  IPriceImprovementBlotterItem,
  QuoteSensitivity,
} from './priceImprovementBlotter.model';

export type PriceImprovementBlotterAction =
  | PriceImprovementBlotterColumnStateChanged
  | PriceImprovementBlotterItemsPerPageChanged
  | PriceImprovementBlotterPageChanged
  | PriceImprovementBlotterColumnDefinitionReceived
  | PriceImprovementBlotterPreferenceStateGet
  | PriceImprovementBlotterPreferenceStatePersisted
  | PriceImprovementBlotterPreferenceStateLoaded
  | PriceImprovementBlotterPreferenceStateLoadingFailed
  | PriceImprovementBlotterItemsReceived
  | PriceImprovementBlotterFiltersLoaded
  | PriceImprovementBlotterFiltersLoadingFailed
  | PriceImprovementBlotterFilterChanged
  | PriceImprovementBlotterAdvancedFiltersToggled
  | PriceImprovementBlotterColumnDefinitionFailed
  | PriceImprovementBlotterItemsRequested
  | PriceImprovementBlotterItemsFetchingFailed
  | PriceImprovementBlotterSelectionChanged
  | PriceImprovementBlotterAcknowledgeRequested
  | PriceImprovementBlotterAcknowledgeSucceeded
  | PriceImprovementBlotterAcknowledgeFailed
  | PriceImprovementBlotterDetailOpened
  | PriceImprovementBlotterDetailClosed
  | PriceImprovementSensitivityReceived
  | PriceImprovementSensitivityFailed
  | PriceImprovementBlotterAcknowledgeErrorDismissed;

//////////////////////////////////////////////////

export function priceImprovementBlotterPageChanged(page: number) {
  return {
    type: 'PRICE_IMPROVEMENT_BLOTTER_PAGE_CHANGED' as const,
    page,
  };
}
export type PriceImprovementBlotterPageChanged = ReturnType<typeof priceImprovementBlotterPageChanged>;

//////////////////////////////////////////////////
export function priceImprovementBlotterItemsPerPageChanged(itemsPerPage: number) {
  return {
    type: 'PRICE_IMPROVEMENT_BLOTTER_ITEMS_PER_PAGE_CHANGED' as const,
    itemsPerPage,
  };
}
export type PriceImprovementBlotterItemsPerPageChanged = ReturnType<typeof priceImprovementBlotterItemsPerPageChanged>;

//////////////////////////////////////////////////

export function priceImprovementBlotterColumnStateChanged(
  columnState: readonly ColumnPreferenceState[],
  columnGroupState?: readonly ColumnGroupPreferenceState[],
) {
  return { type: 'PRICE_IMPROVEMENT_BLOTTER_COLUMN_STATE_CHANGED' as const, columnState, columnGroupState };
}
export type PriceImprovementBlotterColumnStateChanged = ReturnType<typeof priceImprovementBlotterColumnStateChanged>;

//////////////////////////////////////////////////
export function priceImprovementBlotterColumnDefinitionReceived(
  columnDefinition: readonly IBlotterColumnDefinition<IBlotterPriceImprovementItem>[],
) {
  return {
    type: 'PRICE_IMPROVEMENT_BLOTTER_COLUMN_DEFINITION_RECEIVED' as const,
    columnDefinition,
  };
}
export type PriceImprovementBlotterColumnDefinitionReceived = ReturnType<
  typeof priceImprovementBlotterColumnDefinitionReceived
>;

//////////////////////////////////////////////////
export function priceImprovementBlotterPreferenceStateGet() {
  return { type: 'PRICE_IMPROVEMENT_BLOTTER_PREFERENCE_STATE_GET' as const };
}

export type PriceImprovementBlotterPreferenceStateGet = ReturnType<typeof priceImprovementBlotterPreferenceStateGet>;
//////////////////////////////////////////////////
export function priceImprovementBlotterPreferenceStatePersisted(blotterPreferenceState: IBlotterPreferenceState) {
  return { type: 'PRICE_IMPROVEMENT_BLOTTER_PREFERENCE_STATE_PERSISTED' as const, blotterPreferenceState };
}
export type PriceImprovementBlotterPreferenceStatePersisted = ReturnType<
  typeof priceImprovementBlotterPreferenceStatePersisted
>;

//////////////////////////////////////////////////
export function priceImprovementBlotterPreferenceStateLoaded(blotterPreferenceState: IBlotterPreferenceState) {
  return { type: 'PRICE_IMPROVEMENT_BLOTTER_PREFERENCE_STATE_LOADED' as const, blotterPreferenceState };
}
export type PriceImprovementBlotterPreferenceStateLoaded = ReturnType<
  typeof priceImprovementBlotterPreferenceStateLoaded
>;

export function priceImprovementBlotterPreferenceStateLoadingFailed() {
  return {
    type: 'PRICE_IMPROVEMENT_BLOTTER_PREFERENCE_STATE_LOADING_FAILED' as const,
  };
}
export type PriceImprovementBlotterPreferenceStateLoadingFailed = ReturnType<
  typeof priceImprovementBlotterPreferenceStateLoadingFailed
>;

//////////////////////////////////////////////////
export function priceImprovementBlotterItemsReceived(
  items: readonly IBlotterPriceImprovementItem[],
  totalItems: number,
  totalPages: number,
) {
  return {
    type: 'PRICE_IMPROVEMENT_BLOTTER_ITEMS_RECEIVED' as const,
    items,
    totalItems,
    totalPages,
  };
}
export type PriceImprovementBlotterItemsReceived = ReturnType<typeof priceImprovementBlotterItemsReceived>;

//////////////////////////////////////////////////
export function priceImprovementBlotterFiltersLoaded(filters: IPriceImprovementBlotterFilters) {
  return {
    type: 'PRICE_IMPROVEMENT_BLOTTER_FILTERS_LOADED' as const,
    filters,
  };
}
export type PriceImprovementBlotterFiltersLoaded = ReturnType<typeof priceImprovementBlotterFiltersLoaded>;

//////////////////////////////////////////////////
export function priceImprovementBlotterFiltersLoadingFailed() {
  return {
    type: 'PRICE_IMPROVEMENT_BLOTTER_FILTERS_LOADING_FAILED' as const,
  };
}
export type PriceImprovementBlotterFiltersLoadingFailed = ReturnType<
  typeof priceImprovementBlotterFiltersLoadingFailed
>;

//////////////////////////////////////////////////
type Filter = keyof IPriceImprovementBlotterFilters;

interface IPriceImprovementBlotterFilterChanged<F extends Filter> {
  type: 'PRICE_IMPROVEMENT_BLOTTER_FILTER_CHANGED';
  filter: F;
  value: IPriceImprovementBlotterFilters[F];
}
export type PriceImprovementBlotterFilterChanged = IPriceImprovementBlotterFilterChanged<Filter>;

export function priceImprovementBlotterFilterChanged<F extends Filter>(
  filter: F,
  value: IPriceImprovementBlotterFilters[F],
): PriceImprovementBlotterFilterChanged {
  return {
    type: 'PRICE_IMPROVEMENT_BLOTTER_FILTER_CHANGED' as const,
    filter,
    value,
  };
}

//////////////////////////////////////////////////
export function priceImprovementBlotterAdvancedFiltersToggled() {
  return {
    type: 'PRICE_IMPROVEMENT_BLOTTER_ADVANCED_FILTERS_TOGGLED' as const,
  };
}
export type PriceImprovementBlotterAdvancedFiltersToggled = ReturnType<
  typeof priceImprovementBlotterAdvancedFiltersToggled
>;

//////////////////////////////////////////////////
export function priceImprovementBlotterColumnDefinitionFailed() {
  return { type: 'PRICE_IMPROVEMENT_BLOTTER_COLUMN_DEFINITION_FAILED' as const };
}
export type PriceImprovementBlotterColumnDefinitionFailed = ReturnType<
  typeof priceImprovementBlotterColumnDefinitionFailed
>;

//////////////////////////////////////////////////
export function priceImprovementBlotterItemsRequested() {
  return { type: 'PRICE_IMPROVEMENT_BLOTTER_ITEMS_REQUESTED' as const };
}
export type PriceImprovementBlotterItemsRequested = ReturnType<typeof priceImprovementBlotterItemsRequested>;
////////////////////////////////////////////////
export function priceImprovementBlotterItemsFetchingFailed(error: string) {
  return { type: 'PRICE_IMPROVEMENT_BLOTTER_ITEMS_FETCHING_FAILED' as const, error };
}
export type PriceImprovementBlotterItemsFetchingFailed = ReturnType<typeof priceImprovementBlotterItemsFetchingFailed>;

export function priceImprovementBlotterSelectionChanged(selectedRowIds: string[]) {
  return {
    type: 'PRICE_IMPROVEMENT_BLOTTER_SELECTION_CHANGED' as const,
    selectedRowIds,
  };
}
export type PriceImprovementBlotterSelectionChanged = ReturnType<typeof priceImprovementBlotterSelectionChanged>;

export function priceImprovementBlotterAcknowledgeRequested() {
  return {
    type: 'PRICE_IMPROVEMENT_BLOTTER_ACKNOWLEDGE_REQUESTED' as const,
  };
}
export type PriceImprovementBlotterAcknowledgeRequested = ReturnType<
  typeof priceImprovementBlotterAcknowledgeRequested
>;

export function priceImprovementBlotterAcknowledgeSucceeded() {
  return {
    type: 'PRICE_IMPROVEMENT_BLOTTER_ACKNOWLEDGE_SUCCEEDED' as const,
  };
}
export type PriceImprovementBlotterAcknowledgeSucceeded = ReturnType<
  typeof priceImprovementBlotterAcknowledgeSucceeded
>;

export function priceImprovementBlotterAcknowledgeFailed(error: ApiError) {
  return {
    type: 'PRICE_IMPROVEMENT_BLOTTER_ACKNOWLEDGE_FAILED' as const,
    error,
  };
}
export type PriceImprovementBlotterAcknowledgeFailed = ReturnType<typeof priceImprovementBlotterAcknowledgeFailed>;

export function priceImprovementBlotterAcknowledgeErrorDismissed() {
  return {
    type: 'PRICE_IMPROVEMENT_BLOTTER_ACKNOWLEDGE_ERROR_DISMISSED' as const,
  };
}
export type PriceImprovementBlotterAcknowledgeErrorDismissed = ReturnType<
  typeof priceImprovementBlotterAcknowledgeErrorDismissed
>;

export function priceImprovementBlotterDetailOpened(item: IPriceImprovementBlotterItem) {
  return {
    type: 'PRICE_IMPROVEMENT_BLOTTER_DETAIL_OPENED' as const,
    item,
  };
}
export type PriceImprovementBlotterDetailOpened = ReturnType<typeof priceImprovementBlotterDetailOpened>;

export function priceImprovementBlotterDetailClosed() {
  return {
    type: 'PRICE_IMPROVEMENT_BLOTTER_DETAIL_CLOSED' as const,
  };
}
export type PriceImprovementBlotterDetailClosed = ReturnType<typeof priceImprovementBlotterDetailClosed>;

export function priceImprovementSensitivityReceived(id: string, sensitivity: QuoteSensitivity) {
  return {
    type: 'PRICE_IMPROVEMENT_SENSITIVITY_RECEIVED' as const,
    id,
    sensitivity,
  };
}
export type PriceImprovementSensitivityReceived = ReturnType<typeof priceImprovementSensitivityReceived>;

export function priceImprovementSensitivityFailed(id: string, error: string) {
  return {
    type: 'PRICE_IMPROVEMENT_SENSITIVITY_FAILED' as const,
    id,
    error,
  };
}
export type PriceImprovementSensitivityFailed = ReturnType<typeof priceImprovementSensitivityFailed>;
